/**
 * Shared notification-profile helpers for the Maintenance plan modals.
 *
 * Extracted from EditPlanModal (Session 45) so the CreateMaintenancePlan
 * modal can offer the same profile picker at create time (GAP-10). The
 * audit's costliest create-modal gap is that a plan was born with an
 * UNSET notificationConfig — which the notification gate reads as
 * fail-closed / all-off. That's correct by luck for an EMCOR-style
 * Corrigo account and WRONG by default for a self-service HOA. Letting
 * the creator pick a posture at birth fixes that.
 *
 * A profile stamps the full event x recipient matrix in one click:
 *   - corrigo_managed  — silent (client manages comms in their system)
 *   - self_service     — every event -> plan primary contact
 *   - mixed            — milestones only -> plan primary contact
 *   - custom           — hand-tuned matrix (Edit modal only)
 */

export const NOTIFICATION_EVENTS: Array<{ key: string; label: string }> = [
  { key: "wo-received", label: "WO received" },
  { key: "quote-sent", label: "Quote sent" },
  { key: "quote-accepted", label: "Quote accepted" },
  { key: "project-scheduled", label: "Project scheduled" },
  { key: "project-in-flight", label: "Project in flight" },
  { key: "project-completed", label: "Project completed" },
  { key: "invoice-sent", label: "Invoice sent" },
  { key: "invoice-paid", label: "Invoice paid" },
  { key: "weekly-plan-digest", label: "Weekly digest" },
];

export const NOTIFICATION_RECIPIENTS: Array<{ key: string; label: string }> = [
  { key: "plan-primary-contact", label: "Plan primary contact" },
  { key: "per-site-contact", label: "Per-site contact" },
  { key: "submitting-user", label: "Submitting user" },
];

export type NotificationProfile =
  | "corrigo_managed"
  | "self_service"
  | "mixed"
  | "custom";

export const PROFILE_LABELS: Record<NotificationProfile, string> = {
  corrigo_managed: "Corrigo-managed",
  self_service: "Self-service",
  mixed: "Mixed",
  custom: "Custom",
};

export const PROFILE_DESCRIPTIONS: Record<NotificationProfile, string> = {
  corrigo_managed:
    "Silent. Client manages every communication in their own system (Corrigo, etc.). Best for EMCOR-style FM accounts.",
  self_service:
    "Every event notifies the plan primary contact. Best for HOA / church / single-site accounts where the plan contact IS the client.",
  mixed:
    "Milestones only — project completed, invoice sent, invoice paid, weekly digest — to the plan primary contact. Best for Kompan / Goddard-style accounts where the client watches progress but doesn't want intake noise.",
  custom: "Fine-tune every event × recipient checkbox individually.",
};

const key = (e: string, r: string) => `${e}.${r}`;

export function emptyMatrix(): Record<string, boolean> {
  return Object.fromEntries(
    NOTIFICATION_EVENTS.flatMap((e) =>
      NOTIFICATION_RECIPIENTS.map((r) => [key(e.key, r.key), false]),
    ),
  ) as Record<string, boolean>;
}

export function profileMatrix(profile: NotificationProfile): Record<string, boolean> {
  const m = emptyMatrix();

  if (profile === "corrigo_managed") return m;

  if (profile === "self_service") {
    for (const e of NOTIFICATION_EVENTS) {
      m[key(e.key, "plan-primary-contact")] = true;
    }
    return m;
  }

  if (profile === "mixed") {
    for (const eKey of [
      "project-completed",
      "invoice-sent",
      "invoice-paid",
      "weekly-plan-digest",
    ]) {
      m[key(eKey, "plan-primary-contact")] = true;
    }
    return m;
  }

  return m; // custom starts empty; user fills in
}

export function detectProfile(
  config: Record<string, boolean> | null | undefined,
): NotificationProfile {
  const current = config || {};
  const norm = (m: Record<string, boolean>) =>
    Object.entries(m)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .sort()
      .join("|");

  const currentSig = norm(current);
  for (const p of ["corrigo_managed", "self_service", "mixed"] as NotificationProfile[]) {
    if (norm(profileMatrix(p)) === currentSig) return p;
  }
  return "custom";
}

/**
 * Compact the full boolean matrix down to only the true keys, matching
 * how the backend stores notificationConfig (absent = false, keeps the
 * row small). Callers that want an explicit all-silent posture should
 * still store an empty object {} (deliberate silence) rather than null
 * (unconfigured), so the notification gate can tell the two apart.
 */
export function compactMatrix(
  matrix: Record<string, boolean>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(matrix || {})) {
    if (v) out[k] = true;
  }
  return out;
}
